/**
 * IndexedDB adapter for web platform (persistent storage)
 *
 * Implements the flat 4-method storage interface (get/set/remove/clear),
 * matching LocalStorageAdapter shape. The 3-segment namespacing expected by
 * session-manager is handled upstream by the `Store` wrapper
 * (realm/packages/apps/myagent/myagent_ui/src/utils/store.ts), which builds
 * keys as `myagent_store:<namespace>:<userId>:<collection>`.
 *
 * D4 (legacy data invalidation): on construction, one-shot sweep of
 * `localStorage` — remove every key prefixed with `myagent_store:`. Idempotent.
 *
 * D5 (quota guard): before every `set`, consult `navigator.storage.estimate()`.
 *   - usage >= 95% → throw a QuotaExceededError-named Error and invoke
 *     the injected toast callback (if any) with a user-facing message.
 *   - usage >= 80% (and < 95%) → evict the oldest conversation-message entry
 *     by scanning the object store in insertion order for the first key
 *     matching /^myagent_store:[^:]+:[^:]+:conv:/, delete it, `console.warn`
 *     with the word "evict". No match → no-op (do not throw).
 */

declare const indexedDB: any
declare const localStorage: any
declare const navigator: any

const DEFAULT_DB = 'myagent_db'
const DEFAULT_STORE = 'myagent_kv'
const LEGACY_LOCALSTORAGE_PREFIX = 'myagent_store:'
const CONV_KEY_PATTERN = /^myagent_store:[^:]+:[^:]+:conv:/
const SOFT_WATERMARK = 0.8
const HARD_WATERMARK = 0.95
const QUOTA_TOAST = '本地存储已满，请手动清理或清空数据'

export interface IndexedDBAdapterOptions {
  dbName?: string
  storeName?: string
  toast?: (msg: string) => void
}

export class IndexedDBAdapter {
  dbName: string
  storeName: string
  toast?: (msg: string) => void
  private _dbPromise: Promise<any> | null = null

  constructor(opts: IndexedDBAdapterOptions = {}) {
    this.dbName = opts.dbName || DEFAULT_DB
    this.storeName = opts.storeName || DEFAULT_STORE
    this.toast = opts.toast
    this._cleanupLegacyLocalStorage()
  }

  private _cleanupLegacyLocalStorage(): void {
    try {
      if (typeof localStorage === 'undefined' || !localStorage) return
      const toRemove: string[] = []
      const len = typeof localStorage.length === 'number' ? localStorage.length : 0
      for (let i = 0; i < len; i++) {
        const k = localStorage.key(i)
        if (typeof k === 'string' && k.startsWith(LEGACY_LOCALSTORAGE_PREFIX)) {
          toRemove.push(k)
        }
      }
      for (const k of toRemove) {
        try {
          localStorage.removeItem(k)
        } catch {
          /* ignore single-key failure */
        }
      }
    } catch (error) {
      console.error('[IndexedDBAdapter] legacy localStorage cleanup failed:', error)
    }
  }

  private _openDb(): Promise<any> {
    if (this._dbPromise) return this._dbPromise
    this._dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(this.dbName, 1)
      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName)
        }
      }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
    return this._dbPromise
  }

  private _tx(mode: 'readonly' | 'readwrite'): Promise<{ tx: any; store: any }> {
    return this._openDb().then(db => {
      const tx = db.transaction(this.storeName, mode)
      const store = tx.objectStore(this.storeName)
      return { tx, store }
    })
  }

  private static _req<T>(req: any): Promise<T> {
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  }

  async get(key: string): Promise<string | null> {
    try {
      const { store } = await this._tx('readonly')
      const val = await IndexedDBAdapter._req<any>(store.get(key))
      return val === undefined || val === null ? null : (val as string)
    } catch (error) {
      console.error(`[IndexedDBAdapter] Failed to get key "${key}":`, error)
      return null
    }
  }

  async set(key: string, value: string): Promise<void> {
    try {
      const usagePct = await this._usagePct()
      if (usagePct !== null && usagePct >= HARD_WATERMARK) {
        if (this.toast) {
          try {
            this.toast(QUOTA_TOAST)
          } catch {
            /* ignore toast failure */
          }
        }
        const err: any = new Error(
          `QuotaExceededError: storage usage ${(usagePct * 100).toFixed(1)}% exceeds hard limit`,
        )
        err.name = 'QuotaExceededError'
        throw err
      }
      if (usagePct !== null && usagePct >= SOFT_WATERMARK) {
        await this._evictOldestConvEntry()
      }
      const { store } = await this._tx('readwrite')
      await IndexedDBAdapter._req(store.put(value, key))
    } catch (error: any) {
      if (error && error.name === 'QuotaExceededError') throw error
      console.error(`[IndexedDBAdapter] Failed to set key "${key}":`, error)
      throw error
    }
  }

  async remove(key: string): Promise<void> {
    try {
      const { store } = await this._tx('readwrite')
      await IndexedDBAdapter._req(store.delete(key))
    } catch (error) {
      console.error(`[IndexedDBAdapter] Failed to remove key "${key}":`, error)
    }
  }

  async close(): Promise<void> {
    if (!this._dbPromise) return
    try {
      const db = await this._dbPromise
      db.close()
    } catch {
      /* ignore close failure */
    } finally {
      this._dbPromise = null
    }
  }

  async clear(keys?: string[]): Promise<void> {
    try {
      const { store } = await this._tx('readwrite')
      if (keys && Array.isArray(keys)) {
        await Promise.all(keys.map(k => IndexedDBAdapter._req(store.delete(k))))
      } else {
        await IndexedDBAdapter._req(store.clear())
      }
    } catch (error) {
      console.error('[IndexedDBAdapter] Failed to clear storage:', error)
    }
  }

  private async _usagePct(): Promise<number | null> {
    try {
      if (
        typeof navigator === 'undefined' ||
        !navigator ||
        !navigator.storage ||
        typeof navigator.storage.estimate !== 'function'
      ) {
        return null
      }
      const { usage, quota } = await navigator.storage.estimate()
      if (typeof usage !== 'number' || typeof quota !== 'number' || quota <= 0) return null
      return usage / quota
    } catch {
      return null
    }
  }

  private async _evictOldestConvEntry(): Promise<void> {
    try {
      const { store } = await this._tx('readwrite')
      const evictedKey: string | null = await new Promise((resolve, reject) => {
        const cursorReq = store.openCursor()
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result
          if (!cursor) {
            resolve(null)
            return
          }
          const k = typeof cursor.key === 'string' ? cursor.key : String(cursor.key)
          if (CONV_KEY_PATTERN.test(k)) {
            const delReq = cursor.delete()
            delReq.onsuccess = () => resolve(k)
            delReq.onerror = () => reject(delReq.error)
            return
          }
          cursor.continue()
        }
        cursorReq.onerror = () => reject(cursorReq.error)
      })
      if (evictedKey) {
        console.warn(`[IndexedDBAdapter] evict oldest conv entry: ${evictedKey}`)
      }
    } catch (error) {
      console.error('[IndexedDBAdapter] evict scan failed:', error)
    }
  }
}

export const createIndexedDBAdapter = (opts?: IndexedDBAdapterOptions): IndexedDBAdapter =>
  new IndexedDBAdapter(opts)
